import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { pluralizeToString } from 'lib/string/pluralize'
import React from 'react'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'

interface Props {
  fromPrice: number
  duration: number
  withFlights?: boolean
  roomCount: number
  special?: boolean
  specialName?: string
  displayMemberPrice: boolean
  hasLoyaltyRoomUpgrade?: boolean
  saleUnit?: string
}

function OfferCalendarSidebarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    roomCount,
    special,
    specialName = 'Limited time special',
    displayMemberPrice,
    hasLoyaltyRoomUpgrade,
    saleUnit,
  } = props

  const averagePrice = Math.ceil(fromPrice / (roomCount > 1 ? roomCount : duration))

  const displayUnit = saleUnit ?? 'room'

  const unit = roomCount > 1 ? (saleUnit ?? 'room') : 'night'

  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
    >
      {!withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice && <BodyText weight="bold" colour="lux-plus" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString(displayUnit, roomCount)}, `}
        {pluralizeToString('night', duration)} from{' '}
        <b><FormatCurrency value={fromPrice} /></b> total{' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice && <BodyText weight="bold" colour="lux-plus" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString(displayUnit, roomCount)}, `}
        {pluralizeToString('night', duration)} + round trip flights from{' '}
        <b><FormatCurrency value={fromPrice} />*</b> (2 adults){' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      <Group direction="horizontal" gap={24}>
        {special && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidFireIcon colour="urgency" />}
        >
          {specialName}
        </CaptionTextBlock>}
        {hasLoyaltyRoomUpgrade && canLoyaltyUpgrade && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidCircleIcon colour={`tier-${tier}`} />}
        >
          Free upgrade available
        </CaptionTextBlock>}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    displayMemberPrice: state.displayMemberPrice,
    saleUnit: state.offer?.saleUnit,
  }
}, OfferCalendarSidebarPriceBlurb)
